import Head from 'next/head';
import dynamic from 'next/dynamic';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { AppProps } from 'next/app';
import { NextPage } from 'next';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getSettingsFromCookies, HistoryProvider, MaintenanceIllustration } from '@sdf/base';
import { SettingsProviderProps, ThemeProviderProps } from '../@types';
import { AuthProvider } from '../context/AuthProvider';
import { NotificationProvider } from '../context/NotificationProvider';
import { CreateVideoProvider } from '../context/CreateVideoProvider';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/browser';
import { CreditProvider } from '../context/CreditProvider';
import { AppProvider } from '../context/AppContext';
import axios from 'axios';
import { Box, Stack } from '@mui/system';
import ANALYTICS from '../services/analytics';
import 'react-country-state-city/dist/react-country-state-city.css';
import './styles.css';
import { GuestVerifyGate } from '../components/dashboard/GuestVerifyGate';
import { storeClickIds, storeUtmParams } from '../utils/marketing/allAdsPlatformTracking';
import TourWrapper from '../components/tour/TourWrapper';
import { StyleSheetManager } from 'styled-components';

const SettingsProvider = dynamic(
  () => import('@sdf/base').then((mod) => mod.SettingsProvider as React.FC<SettingsProviderProps>),
  {
    ssr: false,
  }
);
const ThemeProvider = dynamic(
  () => import('@sdf/base').then((mod) => mod.ThemeProvider as React.FC<ThemeProviderProps>),
  {
    ssr: false,
  }
);

const SnackbarProvider = dynamic(() => import('notistack').then((mod) => mod.SnackbarProvider), {
  ssr: false,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 3000,
    },
  },
});

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

Sentry.init({
  // enabled: true,
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  // Session Replay
  // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  tracesSampleRate: 1.0,
  // environment: 'local',
});

declare global {
  interface Window {
    PostAffTracker?: {
      setAccountId: (id: string) => void;
      track: () => void;
    };
  }
}

function CustomApp({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const path = router.pathname;
  const pagesWithCreateVideoProvider = ['/create-video', '/create-video-details'];
  const isCreateVideoProviderRequired = pagesWithCreateVideoProvider.includes(path);

  const getLayout = Component.getLayout ?? ((page) => page);
  const [isBackendDown, setIsBackendDown] = useState(false);

  const checkIsBackendDown = async () => {
    try {
      console.log('Checking backend status:', process.env['NEXT_PUBLIC_API_BASE_URL']);
      await axios.get(process.env['NEXT_PUBLIC_API_BASE_URL'] + '/api/check-backend-status', {
        timeout: 10000,
      });
      setIsBackendDown(true); // 200 status -> authentication not working -> Backend is down
    } catch (error) {
      if (error?.response?.request?.status !== 401) {
        // 401 status -> authentication working -> Backend is UP
        setIsBackendDown(true); // if getting some other response -> some issue in backend -> Backend down.
      }
    }
  };
  useEffect(() => {
    checkIsBackendDown();
  }, []);

  useEffect(() => {
    if (ANALYTICS && typeof ANALYTICS.page === 'function') {
      ANALYTICS.page();
    }

    const handleRouteChange = (url) => {
      if (ANALYTICS && typeof ANALYTICS.page === 'function') {
        ANALYTICS.page({ path: url });
      }
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    // Store click IDs from URL if present
    storeClickIds();
    // Store raw UTM params for backend signup sync.
    storeUtmParams();
  }, []);

  const shouldForwardStyledProp = (propName: string) => propName !== 'overRide';

  return (
    <>
      <Head>
        <title>TXT2create</title>
      </Head>
      {isBackendDown ? (
        <main>
          <Stack
            sx={{
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              padding: 2,
            }}
          >
            <Box sx={{ width: { md: '30%' }, textAlign: 'center' }}>
              <MaintenanceIllustration />
              <h3 style={{ color: 'white' }}>
                Site Under Maintainance. Please come back after some time.
              </h3>
            </Box>
          </Stack>
        </main>
      ) : (
        <StyleSheetManager {...({ shouldForwardProp: shouldForwardStyledProp } as any)}>
          <main className="app" style={{ backgroundColor: 'black' }}>
            <SettingsProvider defaultSettings={getSettingsFromCookies()}>
              <ThemeProvider>
                <NotificationProvider>
                  <SnackbarProvider anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <QueryClientProvider client={queryClient}>
                      <AuthProvider>
                        <HistoryProvider>
                          <AppProvider>
                            <Hydrate state={pageProps.dehydratedState}>
                              <CreditProvider>
                                <CreateVideoProvider disabled={!isCreateVideoProviderRequired}>
                                  <TourWrapper router={router}>
                                    {getLayout(<Component {...pageProps} />)}
                                  </TourWrapper>
                                </CreateVideoProvider>
                                {/** Global modal gate for guest users */}
                                {typeof window !== 'undefined' && (
                                  // Keep this outside TourWrapper to avoid duplicate mount/enqueue cycles.
                                  <div id="guest-verify-gate-root">
                                    <GuestVerifyGate />
                                  </div>
                                )}
                              </CreditProvider>
                            </Hydrate>
                          </AppProvider>
                        </HistoryProvider>
                      </AuthProvider>
                    </QueryClientProvider>
                  </SnackbarProvider>
                </NotificationProvider>
              </ThemeProvider>
            </SettingsProvider>
          </main>
        </StyleSheetManager>
      )}
    </>
  );
}

export default CustomApp;
